import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import {
  buildSrhtFinalEvaluation,
  getSrhtExamGradeToneFromPoints,
} from './srhtExamFinalEvaluation';

function getResultChip(candidate) {
  if (!candidate.complete) {
    return { label: 'Noch offen', color: 'warning' };
  }

  return candidate.passed
    ? { label: 'Bestanden', color: 'success' }
    : { label: 'Nicht bestanden', color: 'error' };
}

function buildDialogState(candidate = null) {
  return {
    open: Boolean(candidate),
    candidateId: candidate?.candidateId ? String(candidate.candidateId) : '',
    stationCode: candidate?.oralRetake?.record?.stationCode || candidate?.oralRetake?.recommendedDiscipline?.code || '',
    taskText: candidate?.oralRetake?.record?.taskText || '',
    responseText: candidate?.oralRetake?.record?.responseText || '',
  };
}

export default function SrhtExamFinalEvaluationPanel({
  examDetail,
  canEdit,
  isLocked,
  actionCandidateId,
  allResultsReleaseBusy,
  onSaveOralRetake,
  onTogglePersonalResultsRelease,
  onToggleAllResultsRelease,
}) {
  const evaluation = useMemo(() => buildSrhtFinalEvaluation(examDetail || {}), [examDetail]);
  const [dialogState, setDialogState] = useState(buildDialogState(null));
  const personalResultsReleased = Boolean(examDetail?.exam?.personalResultsReleased);
  const allResultsReleased = Boolean(examDetail?.exam?.allResultsReleased || examDetail?.exam?.all_results_released);

  const selectedCandidate = useMemo(
    () => evaluation.candidates.find((candidate) => String(candidate.candidateId) === String(dialogState.candidateId || '')) || null,
    [dialogState.candidateId, evaluation.candidates],
  );

  const selectedStation = useMemo(
    () => selectedCandidate?.disciplines?.find((discipline) => discipline.code === dialogState.stationCode) || null,
    [dialogState.stationCode, selectedCandidate],
  );

  const canSaveDraft = Boolean(dialogState.taskText.trim() && selectedCandidate && selectedStation);
  const canSubmitDecision = Boolean(dialogState.taskText.trim() && dialogState.responseText.trim() && selectedCandidate && selectedStation);

  const openDialog = (candidate) => {
    setDialogState(buildDialogState(candidate));
  };

  const closeDialog = () => {
    setDialogState(buildDialogState(null));
  };

  const handleSubmit = async (outcome) => {
    const requiresResponse = outcome === 'passed' || outcome === 'failed';
    const canSubmit = requiresResponse ? canSubmitDecision : canSaveDraft;
    if (!selectedCandidate || !selectedStation || !canSubmit) {
      return;
    }

    await onSaveOralRetake(selectedCandidate.candidateId, {
      stationCode: selectedStation.code,
      taskText: dialogState.taskText.trim(),
      responseText: dialogState.responseText.trim(),
      outcome,
    });
    closeDialog();
  };

  if (!examDetail?.exam?.id) {
    return <Alert severity="info">Bitte zuerst eine Prüfung SRHT auswählen oder anlegen.</Alert>;
  }

  if ((examDetail?.candidates || []).length === 0) {
    return <Alert severity="info">Für diese Prüfung wurden noch keine Bewerber übernommen.</Alert>;
  }

  if (evaluation.candidates.length === 0) {
    return <Alert severity="info">Für Sie liegen noch keine freigegebenen Abschlussauswertungen vor.</Alert>;
  }

  return (
    <Stack spacing={2}>
      {canEdit ? (
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'center' }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
            <Button
              variant={personalResultsReleased ? 'outlined' : 'contained'}
              color={personalResultsReleased ? 'warning' : 'primary'}
              onClick={onTogglePersonalResultsRelease}
              disabled={!canEdit || isLocked || allResultsReleaseBusy}
            >
              {allResultsReleaseBusy
                ? 'Speichert...'
                : (personalResultsReleased ? 'Freigabe persönliche Ergebnisse zurücknehmen' : 'Freigabe persönliche Ergebnisse')}
            </Button>
            <Button
              variant={allResultsReleased ? 'outlined' : 'contained'}
              color={allResultsReleased ? 'warning' : 'primary'}
              onClick={onToggleAllResultsRelease}
              disabled={!canEdit || isLocked || allResultsReleaseBusy}
            >
              {allResultsReleaseBusy
                ? 'Speichert...'
                : (allResultsReleased ? 'Freigabe aller Ergebnisse zurücknehmen' : 'Freigabe aller Ergebnisse')}
            </Button>
          </Stack>
        </Stack>
      ) : null}

      {evaluation.completedCandidateCount === 0 ? (
        <Alert severity="info">Es liegen noch keine vollständig abgeschlossenen Prüfungsergebnisse vor. Die Abschlussauswertung aktualisiert sich, sobald schriftliche Prüfung und alle fünf Prüfbögen finalisiert sind.</Alert>
      ) : null}

      {evaluation.candidates.map((candidate) => {
        const resultChip = getResultChip(candidate);
        const oralRecord = candidate.oralRetake.record;
        const showOralButton = canEdit && Boolean(oralRecord || candidate.oralRetake.eligible);
        const bestDiscipline = candidate.disciplines
          .filter((discipline) => discipline.points !== null)
          .sort((left, right) => Number(right.points) - Number(left.points))[0] || null;
        const weakestDiscipline = candidate.disciplines
          .filter((discipline) => discipline.points !== null)
          .sort((left, right) => Number(left.points) - Number(right.points))[0] || null;

        return (
          <Card key={candidate.candidateId}>
            <CardContent>
              <Stack spacing={1.5}>
                <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={1.5}>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                      {`Rang ${candidate.rankPosition}: ${candidate.fullName}`}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {candidate.complete
                        ? `${candidate.percentScore}% · ${candidate.overallPointsRaw} / 15 Punkte · ${candidate.gradeLabel}`
                        : 'Abschlussauswertung noch nicht vollständig'}
                    </Typography>
                  </Box>
                  <Stack spacing={1} alignItems={{ xs: 'flex-start', md: 'flex-end' }}>
                    <Chip label={resultChip.label} color={resultChip.color} />
                    {oralRecord ? (
                      <Chip
                        label={oralRecord.isDraft ? 'Mündlich vorbereitet' : `Mündlich: ${oralRecord.outcome === 'passed' ? 'Bestanden' : 'Nicht bestanden'}`}
                        color={oralRecord.isDraft ? 'warning' : (oralRecord.outcome === 'passed' ? 'success' : 'error')}
                        size="small"
                        variant="outlined"
                      />
                    ) : null}
                    {showOralButton ? (
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => openDialog(candidate)}
                        disabled={String(actionCandidateId || '') === String(candidate.candidateId) || isLocked}
                      >
                        Mündliche Nachprüfung
                      </Button>
                    ) : null}
                  </Stack>
                </Stack>

                <>
                  <Grid container spacing={1}>
                    {candidate.disciplines.map((discipline) => {
                      const showOralRetakeChip = oralRecord?.isDraft !== true
                        && oralRecord?.outcome === 'passed'
                        && oralRecord?.stationCode === discipline.code;

                      return (
                        <Grid item xs={12} sm={6} md={4} key={`${candidate.candidateId}-${discipline.code}`}>
                          <Paper variant="outlined" sx={{ p: 1.25, borderRadius: 2.5, height: '100%' }}>
                            <Stack spacing={0.5}>
                              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{discipline.shortTitle}</Typography>
                              <Typography variant="body2" color="text.secondary">
                                {discipline.points === null ? 'Noch offen' : `${discipline.points} / 15 Punkte`}
                              </Typography>
                              <Typography variant="caption" color={discipline.points !== null && discipline.points < 5 ? 'error.main' : 'text.secondary'}>
                                {discipline.gradeLabel}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">{discipline.rawValueLabel}</Typography>
                              {showOralRetakeChip ? <Chip label="Mündl. nachgeprüft" size="small" color="success" variant="outlined" sx={{ mt: 0.5, width: 'fit-content' }} /> : null}
                            </Stack>
                          </Paper>
                        </Grid>
                      );
                    })}
                  </Grid>

                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {bestDiscipline ? <Chip label={`Beste Disziplin: ${bestDiscipline.shortTitle}`} size="small" /> : null}
                    {weakestDiscipline ? <Chip label={`Schwächste Disziplin: ${weakestDiscipline.shortTitle}`} size="small" color={weakestDiscipline.points !== null && weakestDiscipline.points < 5 ? 'error' : 'default'} /> : null}
                    {candidate.complete ? <Chip label={`Gesamt: ${candidate.overallWholePoints} Punkte`} size="small" color={getSrhtExamGradeToneFromPoints(candidate.overallWholePoints)} variant="outlined" /> : null}
                  </Stack>
                </>
              </Stack>
            </CardContent>
          </Card>
        );
      })}

      <Dialog open={dialogState.open && canEdit} onClose={closeDialog} fullWidth maxWidth="md">
        <DialogTitle>Mündliche Nachprüfung</DialogTitle>
        <DialogContent>
          {selectedCandidate && selectedStation ? (
            <Stack spacing={2} sx={{ pt: 1 }}>
              <Stack spacing={0.75}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{selectedCandidate.fullName}</Typography>
                <Typography variant="body2" color="text.secondary">
                  Vorgeschlagener Prüfungsteil: {selectedStation.title}
                </Typography>
                {selectedCandidate.oralRetake.eligible ? (
                  <Typography variant="body2" color="text.secondary">
                    Bei Bestehen bleibt die Bepunktung unverändert. Die Gesamtprüfung wird dennoch aufgrund der bestandenen mündlichen Nachprüfung als bestanden gewertet.
                  </Typography>
                ) : null}
              </Stack>

              <TextField
                label="Aufgabenstellung"
                value={dialogState.taskText}
                onChange={(event) => setDialogState((current) => ({ ...current, taskText: event.target.value }))}
                multiline
                minRows={4}
                fullWidth
                disabled={!canEdit || isLocked}
              />
              <TextField
                label={`Lösung ${selectedCandidate.fullName}`}
                value={dialogState.responseText}
                onChange={(event) => setDialogState((current) => ({ ...current, responseText: event.target.value }))}
                multiline
                minRows={5}
                fullWidth
                disabled={!canEdit || isLocked}
              />
            </Stack>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>Schließen</Button>
          {canEdit && !isLocked ? (
            <>
              <Button
                variant="outlined"
                onClick={() => handleSubmit('draft')}
                disabled={!canSaveDraft || String(actionCandidateId || '') === String(dialogState.candidateId || '')}
              >
                Speichern
              </Button>
              <Button
                variant="outlined"
                color="error"
                onClick={() => handleSubmit('failed')}
                disabled={!canSubmitDecision || String(actionCandidateId || '') === String(dialogState.candidateId || '')}
              >
                Nicht bestanden
              </Button>
              <Button
                variant="contained"
                color="success"
                onClick={() => handleSubmit('passed')}
                disabled={!canSubmitDecision || String(actionCandidateId || '') === String(dialogState.candidateId || '')}
              >
                Bestanden
              </Button>
            </>
          ) : null}
        </DialogActions>
      </Dialog>
    </Stack>
  );
}